import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'insiderrisk_v2026_seen';

const WelcomeModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'changelog' | 'howitworks'>('changelog');

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setIsOpen(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-blue-900 px-6 py-5 flex items-start justify-between shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-amber-400 text-slate-900 text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                2026 Edition
              </span>
              <span className="text-blue-300 text-xs">What's new + how it works</span>
            </div>
            <h2 id="modal-title" className="text-white text-xl font-bold leading-tight">
              Insider Threat Risk Profiler
            </h2>
          </div>
          <button
            onClick={dismiss}
            aria-label="Close"
            className="text-white/50 hover:text-white transition-colors ml-4 mt-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 shrink-0">
          <button
            onClick={() => setActiveTab('changelog')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'changelog'
                ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            2026 Changelog
          </button>
          <button
            onClick={() => setActiveTab('howitworks')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'howitworks'
                ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            How It Works
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* ── CHANGELOG TAB ── */}
          {activeTab === 'changelog' && (
            <div className="space-y-5">

              {/* Big new feature */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-400 text-slate-900 text-xs font-bold px-2 py-0.5 rounded-full">NEW</span>
                  <h3 className="font-bold text-amber-900">6th Domain: AI & GenAI Controls</h3>
                </div>
                <p className="text-sm text-amber-800 mb-3">
                  The biggest addition to this edition — a full new assessment category with 5 questions covering the most critical insider risk vectors emerging from AI adoption.
                </p>
                <ul className="space-y-1.5">
                  {[
                    ['GenAI Data Leakage Policy', 'Are employees pasting sensitive data into ChatGPT, Gemini, or other LLMs?'],
                    ['Shadow AI Detection', 'Can you see and govern unauthorized AI tool usage across your org?'],
                    ['AI Social Engineering Defense', 'Are your defenses ready for voice cloning and AI-generated spear-phishing?'],
                    ['Agentic AI Risk Management', 'Do you govern autonomous AI agents with access to your systems and data?'],
                    ['Nation-State IT Worker Detection', 'Can you detect DPRK/fraudulent remote hires? (Active CISA/FBI advisory)'],
                  ].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-2 text-sm">
                      <span className="mt-0.5 w-4 h-4 bg-amber-400 rounded-full shrink-0 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className="text-amber-900"><strong>{title}</strong> — {desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Other changelog items */}
              <div className="grid sm:grid-cols-2 gap-3">
                <ChangelogCard
                  badge="UPDATED"
                  badgeColor="blue"
                  title="Emerging Threat Briefing"
                  items={[
                    'Added GenAI Data Exfiltration threat card',
                    'Added Nation-State IT Worker Infiltration card',
                    '6 total threat scenarios now covered',
                  ]}
                />
                <ChangelogCard
                  badge="UPDATED"
                  badgeColor="blue"
                  title="2026 Statistics"
                  items={[
                    '34% of breaches involve insiders (Verizon DBIR)',
                    '$16.2M average annual incident cost (Ponemon)',
                    '76% report GenAI-driven insider risk increase',
                    '308-day average detection time',
                  ]}
                />
                <ChangelogCard
                  badge="UPDATED"
                  badgeColor="blue"
                  title="Industry Coverage"
                  items={[
                    'Added Energy / Critical Infrastructure sector',
                    'All industry contexts refreshed for 2026 threats',
                    'Government/Defense language updated',
                  ]}
                />
                <ChangelogCard
                  badge="FIXED"
                  badgeColor="green"
                  title="Bug Fixes"
                  items={[
                    'Intro screen now shows on first load (was skipped)',
                    'Progress bar now reaches 100% on final step',
                    'Industry/size selectors visible before starting',
                  ]}
                />
              </div>

              {/* Advisory callout */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-red-800">
                  <strong>CISA & FBI Active Advisories (2025–2026):</strong> Multiple warnings issued on North Korean fraudulent IT worker infiltration and GenAI-enabled insider threat vectors. The new AI & GenAI Controls domain directly addresses these advisories.
                </p>
              </div>
            </div>
          )}

          {/* ── HOW IT WORKS TAB ── */}
          {activeTab === 'howitworks' && (
            <div className="space-y-5">

              {/* Overview */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-blue-900 mb-2">Assessment Overview</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[
                    ['30', 'Total Questions'],
                    ['6', 'Domains'],
                    ['5', 'Questions / Domain'],
                  ].map(([num, label]) => (
                    <div key={label} className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="text-2xl font-bold text-blue-700">{num}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scoring */}
              <div>
                <h3 className="font-bold text-gray-800 mb-2">How Answers Are Scored</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Each question has 4 options representing your current control maturity. Selecting an option assigns a score from 0 (highest risk) to 3 (lowest risk):
                </p>
                <div className="space-y-2">
                  {[
                    { score: 0, label: 'Option A — High Risk', desc: 'No control or process in place', color: 'bg-red-500', text: 'text-red-800', bg: 'bg-red-50 border-red-200' },
                    { score: 1, label: 'Option B — Moderate Risk', desc: 'Basic or partial control only', color: 'bg-yellow-500', text: 'text-yellow-800', bg: 'bg-yellow-50 border-yellow-200' },
                    { score: 2, label: 'Option C — Low Risk', desc: 'Solid control with some gaps', color: 'bg-blue-500', text: 'text-blue-800', bg: 'bg-blue-50 border-blue-200' },
                    { score: 3, label: 'Option D — Minimal Risk', desc: 'Advanced, comprehensive control', color: 'bg-green-500', text: 'text-green-800', bg: 'bg-green-50 border-green-200' },
                  ].map(({ score, label, desc, color, text, bg }) => (
                    <div key={score} className={`flex items-center gap-3 p-3 rounded-lg border ${bg}`}>
                      <div className={`w-7 h-7 rounded-full ${color} text-white text-sm font-bold flex items-center justify-center shrink-0`}>
                        {score}
                      </div>
                      <div>
                        <div className={`text-sm font-semibold ${text}`}>{label}</div>
                        <div className="text-xs text-gray-500">{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Calculation */}
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Score Calculation</h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Category Score</div>
                    <div className="font-mono text-sm bg-white border border-gray-200 rounded px-3 py-2 text-blue-800">
                      (sum of 5 answers) ÷ 15 × 100 = %
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Max per category = 5 questions × 3 points = <strong>15 points</strong>. A perfect score = 100%.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Overall Score</div>
                    <div className="font-mono text-sm bg-white border border-gray-200 rounded px-3 py-2 text-blue-800">
                      (sum of all 30 answers) ÷ 90 × 100 = %
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Max overall = 30 questions × 3 points = <strong>90 points</strong>. Unanswered questions count as 0 (highest risk).
                    </p>
                  </div>
                </div>
              </div>

              {/* Maturity levels */}
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Maturity Level Thresholds</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { level: 'Initial', range: '< 25%', color: 'bg-red-100 border-red-300 text-red-800' },
                    { level: 'Developing', range: '25–49%', color: 'bg-yellow-100 border-yellow-300 text-yellow-800' },
                    { level: 'Established', range: '50–74%', color: 'bg-blue-100 border-blue-300 text-blue-800' },
                    { level: 'Optimized', range: '≥ 75%', color: 'bg-green-100 border-green-300 text-green-800' },
                  ].map(({ level, range, color }) => (
                    <div key={level} className={`rounded-lg border p-3 text-center ${color}`}>
                      <div className="font-bold text-sm">{level}</div>
                      <div className="text-xs mt-0.5 opacity-80">{range}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Privacy note */}
              <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <svg className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <div className="text-sm font-semibold text-gray-700">Privacy — Your answers stay with you</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    All scoring runs entirely in your browser. No responses, scores, or personal data are transmitted or stored anywhere. Export your results as a PDF for offline use.
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 shrink-0 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-400">
            This dialog won't appear again on return visits.
          </p>
          <button
            onClick={dismiss}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2 shrink-0"
          >
            Start Assessment
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>

      </div>
    </div>
  );
};

interface ChangelogCardProps {
  badge: string;
  badgeColor: 'blue' | 'green' | 'amber';
  title: string;
  items: string[];
}

const ChangelogCard: React.FC<ChangelogCardProps> = ({ badge, badgeColor, title, items }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors[badgeColor]}`}>{badge}</span>
        <span className="font-semibold text-gray-800 text-sm">{title}</span>
      </div>
      <ul className="space-y-1">
        {items.map(item => (
          <li key={item} className="text-xs text-gray-600 flex items-start gap-1.5">
            <span className="text-gray-300 mt-0.5">•</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default WelcomeModal;
